import { useState } from "react";
import { API, authFetch } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Check, AlertOctagon, FileSpreadsheet, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui-bits";

export default function ImportRecipesPanel() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const reset = () => {
    setFile(null);
    setPreview(null);
  };

  const upload = async (endpoint, setBusy) => {
    if (!file) {
      toast.error("Pick a spreadsheet first");
      return null;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await authFetch(`${API}${endpoint}`, { method: "POST", body: fd });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      return await r.json();
    } catch (e) {
      toast.error(e.message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const doPreview = async () => {
    const data = await upload("/admin/import/recipes/preview/xlsx", setLoading);
    if (data) {
      setPreview(data);
      toast.success(
        `Parsed ${data.total_parsed} row${data.total_parsed === 1 ? "" : "s"}: ${data.new_count} new, ${data.duplicate_count} already exist`
      );
    }
  };

  const doImport = async () => {
    const data = await upload("/admin/import/recipes/xlsx", setImporting);
    if (data) {
      toast.success(
        `Imported ${data.created_count} new recipe${data.created_count === 1 ? "" : "s"}`
      );
      reset();
    }
  };

  return (
    <Panel
      title="Import recipes from Excel"
      action={
        <span className="text-xs uppercase tracking-wider text-stone-500">.xlsx · .csv</span>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-stone-600 dark:text-stone-300">
          Drop in a spreadsheet from your existing system. We auto-detect columns named{" "}
          <span className="font-mono">Name</span> (or <span className="font-mono">Product</span> /{" "}
          <span className="font-mono">Recipe</span>), optionally{" "}
          <span className="font-mono">Code</span>, <span className="font-mono">Yield</span>,{" "}
          <span className="font-mono">Unit</span>, <span className="font-mono">Allergens</span>,{" "}
          <span className="font-mono">Instructions</span>. Duplicates by name are skipped.
        </p>
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const r = await authFetch(
                  `${API}/admin/import/recipes/template.xlsx`
                );
                if (!r.ok) throw new Error("Template download failed");
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "recipe-import-template.xlsx";
                a.click();
                URL.revokeObjectURL(url);
              } catch (e) {
                toast.error(e.message);
              }
            }}
            data-testid="import-template-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Download blank template
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const r = await authFetch(`${API}/admin/export/recipes.xlsx`);
                if (!r.ok) throw new Error("Export failed");
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "recipes-export.xlsx";
                a.click();
                URL.revokeObjectURL(url);
              } catch (e) {
                toast.error(e.message);
              }
            }}
            data-testid="export-all-recipes-btn"
            className="ml-2"
          >
            <Download className="h-4 w-4 mr-2" />
            Download all recipes
          </Button>
        </div>
        <div className="rounded-xl border-2 border-dashed border-border dark:border-stone-700 p-6 flex flex-col items-center gap-3">
          <FileSpreadsheet className="h-10 w-10 text-stone-400" />
          <Input
            type="file"
            accept=".xlsx,.xlsm,.csv"
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setPreview(null);
            }}
            className="max-w-md"
            data-testid="import-file"
          />
          {file && (
            <div className="text-sm font-mono text-stone-700 dark:text-stone-200">
              {file.name} · {(file.size / 1024).toFixed(1)} KB
            </div>
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={doPreview}
              disabled={!file || loading}
              variant="outline"
              data-testid="import-preview-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              {loading ? "Parsing…" : "Preview"}
            </Button>
            <Button
              type="button"
              onClick={doImport}
              disabled={!file || importing || !preview || preview.new_count === 0}
              data-testid="import-submit-btn"
            >
              <Check className="h-4 w-4 mr-2" />
              {importing
                ? "Importing…"
                : preview
                ? `Import ${preview.new_count} new`
                : "Import"}
            </Button>
          </div>
        </div>

        {preview && (
          <div className="space-y-3" data-testid="import-preview">
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-900 border border-emerald-200 font-semibold">
                <Check className="h-3 w-3 inline mr-1" />
                {preview.new_count} new
              </span>
              <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-900 border border-amber-200 font-semibold">
                <AlertOctagon className="h-3 w-3 inline mr-1" />
                {preview.duplicate_count} already exist (will be skipped)
              </span>
            </div>
            <div className="rounded-xl border border-border dark:border-stone-700 overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-stone-500 bg-stone-50/60 dark:bg-stone-800/60 sticky top-0">
                  <tr>
                    <th className="py-2 px-3">#</th>
                    <th>Name</th>
                    <th>Code</th>
                    <th className="text-right">Yield</th>
                    <th>Unit</th>
                    <th>Allergens</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {[...preview.new_rows, ...preview.duplicate_rows].map((r, i) => {
                    const isDup = i >= preview.new_count;
                    return (
                      <tr
                        key={`${r.name}-${i}`}
                        className="border-t border-border dark:border-stone-700 h-10"
                        data-testid={`import-row-${i}`}
                      >
                        <td className="px-3 text-stone-500 tnum">{i + 1}</td>
                        <td className="font-medium">{r.name}</td>
                        <td className="text-stone-600 dark:text-stone-300 font-mono text-xs">
                          {r.product_code || "—"}
                        </td>
                        <td className="text-right tnum">{r.yield_quantity}</td>
                        <td>{r.yield_unit}</td>
                        <td className="text-xs text-amber-700 dark:text-amber-300">
                          {(r.allergens || []).join(", ") || "—"}
                        </td>
                        <td>
                          {isDup ? (
                            <span className="text-amber-700 dark:text-amber-300 font-semibold text-xs">
                              Duplicate
                            </span>
                          ) : (
                            <span className="text-emerald-700 dark:text-emerald-300 font-semibold text-xs">
                              New
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}
